import _regeneratorRuntime from "/vercel/path0/node_modules/@babel/runtime/helpers/esm/regeneratorRuntime.js";
import _asyncToGenerator from "/vercel/path0/node_modules/@babel/runtime/helpers/esm/asyncToGenerator.js";
import _slicedToArray from "/vercel/path0/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import { useEffect, useState } from 'react';
import { useEthers } from './useEthers';
/**
 * Fetches ERC20 token list under a given address and filters them by chain id. Optionally it can filter also by token tags.
 * @public
 * @param tokenListURI URI to fetch token list from
 * @param overrideChainId chain id to filter tokens by (if not specified then current network is used)
 * @param tags list of tags to filter tokens by (token is included if it contains any of given tags)
 * @returns
 *   `name`: `string` - token list name.
 *   `logoURI`: `string` - URI to get token list logo from.
 *   `tokens`: `TokenInfo[]` - list of `TokenInfo` objects.
 *   If an error occurs `undefined` is returned.
 *
 * @example
 * const { name, logoURI, tokens } = useTokenList(UNISWAP_DEFAULT_TOKEN_LIST_URI) || {}
 * const httpSource = logoURI && logoURI.startsWith('ipfs') ? logoURI.replace('ipfs://', 'https://ipfs.io/ipfs/') : logoURI
 * return (
 *   <div>
 *     <div>
 *       {name}
 *       {httpSource && <img src={httpSource} alt={name}/>}
 *     </div>
 *     <ol>
 *       {tokens?.map(token => (
 *         <li>
 *           <ul>
 *             <li>Name: {token.name}</li>
 *             <li>Symbol: {token.symbol}</li>
 *             <li>Decimals: {token.decimals}</li>
 *             <li>Address: {token.address}</li>
 *           </ul>
 *         </li>
 *       ))}
 *     </ol>
 *   </div>
 * )
 * @see [Token lists](https://uniswap.org/blog/token-lists)
 * @see [Token list json example](https://github.com/Uniswap/token-lists/blob/main/test/schema/example.tokenlist.json)
 * @see [`TokenInfo` object](https://github.com/Uniswap/token-lists/blob/main/src/types.ts)
 */
export function useTokenList(tokenListURI, overrideChainId, tags) {
  var _useEthers = useEthers(),
    providerChainId = _useEthers.chainId;
  var _useState = useState(),
    _useState2 = _slicedToArray(_useState, 2),
    tokenList = _useState2[0],
    setTokenList = _useState2[1];
  var chainId = overrideChainId || providerChainId;
  useEffect(function () {
    fetch(tokenListURI).then( /*#__PURE__*/function () {
      var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee(response) {
        var _yield$response$json, name, logoURI, tokens, errorMessage;
        return _regeneratorRuntime().wrap(function _callee$(_context) {
          while (1) switch (_context.prev = _context.next) {
            case 0:
              if (!response.ok) {
                _context.next = 10;
                break;
              }
              _context.next = 3;
              return response.json();
            case 3:
              _yield$response$json = _context.sent;
              name = _yield$response$json.name;
              logoURI = _yield$response$json.logoURI;
              tokens = _yield$response$json.tokens;
              setTokenList({
                name: name,
                logoURI: logoURI,
                tokens: tokens.filter(function (token) {
                  var sameChainId = token.chainId === chainId;
                  if (!tags) {
                    return sameChainId;
                  }
                  return sameChainId && token.tags && token.tags.some(function (tag) {
                    return tags.includes(tag);
                  });
                })
              });
              _context.next = 14;
              break;
            case 10:
              _context.next = 12;
              return response.text();
            case 12:
              errorMessage = _context.sent;
              return _context.abrupt("return", Promise.reject(new Error(errorMessage)));
            case 14:
            case "end":
              return _context.stop();
          }
        }, _callee);
      }));
      return function (_x) {
        return _ref.apply(this, arguments);
      };
    }()).catch(function (err) {
      console.log(err);
      setTokenList(undefined);
    });
  }, [tokenListURI, chainId]);
  return tokenList;
}